import { Button, message } from 'antd';
import Modal, { ModalProps } from 'antd/lib/modal/Modal';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { useEnsureContext } from 'common/hooks';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { MedicalOperator } from 'models/medicalOperator';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { MedicalOperatorPatientsToAssignList } from 'pages/BusinessPartnerManagement/BusinessPartnerMedicalOperatorsManagement/BusinessPartnerMedicalOperatorsPage/MedicalOperatorPatientsToAssignList';
import React, { useState } from 'react';
import { QueryKey } from 'api/queryKey';
import { FormattedMessage } from 'react-intl';
import { useMutation, useQueryCache } from 'react-query';
import styled from 'styled-components';

interface MedicalOperatorsAssignPatientsModalProps extends ModalProps {
  medicalOperator?: MedicalOperator;
  medicalOperatorId: number;
  hideModal: () => void;
}

const StyledModal = styled(Modal)`
  top: 50px;
  width: 90vw !important;

  .ant-modal-body {
    height: 70vh;
    overflow-y: auto;
    overflow-x: hidden;
  }
`;

export const MedicalOperatorsAssignPatientsModal: React.FC<MedicalOperatorsAssignPatientsModalProps> = ({
  medicalOperator,
  medicalOperatorId,
  hideModal,
  ...modalProps
}) => {
  const bpContext = useEnsureContext(BusinessPartnerContext);
  const [checkedPatientsIds, setCheckedPatientsIds] = useState<number[]>([]);
  const queryCache = useQueryCache();

  const [mutate, mutationHelpers] = useMutation(
    BusinessPartnersAPI.assignPatientsToMedicalOperator
  );

  const intlMessage = useFormatMessage();

  const onCancel = () => {
    setCheckedPatientsIds([]);
    hideModal();
  };

  const partnerToBeHardcodedInFilters = medicalOperator?.partnerDetails;

  const assignPatients = (items: number[]) => {
    mutate(
      {
        businessPartnerId: medicalOperator?.partnerDetails
          ? medicalOperator.partnerDetails.id
          : bpContext.id,
        dto: {
          items,
          medicalOperatorId: medicalOperatorId,
        },
      },
      {
        onSuccess: () => {
          queryCache
            .invalidateQueries(
              QueryKey.BusinessPartnerMedicalOperatorPatientsList
            )
            .then();
          message.success(
            intlMessage({ id: 'medicalOperators.addPatients.success' })
          );
        },
      }
    ).then(() => onCancel());
  };

  const footer = [
    <Button id="button-cancel-assign-patients" key="cancel" onClick={onCancel}>
      <FormattedMessage id="cancel" />
    </Button>,
    <Button
      id="button-assign-selected-patients"
      key="assign"
      type="primary"
      disabled={checkedPatientsIds.length === 0 || mutationHelpers.isLoading}
      onClick={() => assignPatients(checkedPatientsIds)}
    >
      <FormattedMessage id="devices.assignSelected" />
    </Button>,
  ];

  return (
    <StyledModal
      destroyOnClose={true}
      title={intlMessage(
        { id: 'medicalOperators.addPatients' },
        {
          firstName: medicalOperator?.firstName,
          lastName: medicalOperator?.lastName,
        }
      )}
      footer={footer}
      onCancel={onCancel}
      {...modalProps}
    >
      <MedicalOperatorPatientsToAssignList
        medicalOperatorId={medicalOperatorId}
        checkedPatientsIds={checkedPatientsIds}
        setCheckedPatientsIds={setCheckedPatientsIds}
        partnerHardcoded={partnerToBeHardcodedInFilters}
      />
    </StyledModal>
  );
};
